import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import {
    X, ArrowRight, ArrowLeft, ShieldCheck, CheckCircle2, Sparkles,
    CalendarRange, BedDouble, Globe2, Receipt, BarChart3, Building2, Mail, UsersRound, Lock,
} from "lucide-react";

// Map string names from the API to Lucide icon components
const ICON_MAP = {
    CalendarRange, BedDouble, Globe2, Receipt, BarChart3, Building2, Mail, UsersRound,
    Lock, ShieldCheck,
};

/**
 * One-time onboarding modal shown right after a user's first successful sign-in.
 * Fetches `/auth/onboarding-tips`; if `should_show === false` the modal stays hidden.
 * Never blocks — the user can dismiss without doing anything.
 */
export default function OnboardingTipsModal() {
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [tips, setTips] = useState([]);
    const [recs, setRecs] = useState([]);
    const [step, setStep] = useState(0);

    const isSq = i18n.language?.startsWith("sq");

    useEffect(() => {
        let cancelled = false;
        // Skip fetching tips while the onboarding wizard is still pending so
        // that OnboardingBanner (dashboard) isn't covered by the modal overlay.
        (async () => {
            try {
                const { data: st } = await api.get("/onboarding/status");
                if (cancelled) return;
                if (st?.needs_wizard) return; // banner will handle it
            } catch { /* proceed to tips fetch — status endpoint is best-effort */ }
            api.get("/auth/onboarding-tips").then(({ data }) => {
                if (cancelled) return;
                if (data?.should_show) {
                    setTips(data.tips || []);
                    setRecs(data.security_recommendations || []);
                    setOpen(true);
                }
            }).catch(() => { /* silent — modal simply doesn't show */ });
        })();
        return () => { cancelled = true; };
    }, []);

    const total = tips.length + 1; // +1 for the security screen at the end
    const isSecurityStep = step === tips.length;

    const dismiss = async () => {
        try { await api.post("/auth/onboarding-tips/dismiss"); } catch { /* noop */ }
        setOpen(false);
    };

    const next = () => {
        if (step < total - 1) setStep((s) => s + 1);
        else dismiss();
    };
    const back = () => setStep((s) => Math.max(0, s - 1));

    const tip = tips[step];

    // Only mount when needed
    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm" data-testid="onboarding-tips-modal">
            <div className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#252E3D] via-[#2b3547] to-[#F26322] p-6 text-white">
                    <button
                        onClick={dismiss}
                        aria-label="Dismiss"
                        data-testid="onboarding-close"
                        className="absolute right-4 top-4 rounded-full bg-white/10 p-1.5 text-white/80 transition hover:bg-white/20 hover:text-white"
                    >
                        <X size={16} />
                    </button>
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70">
                        <Sparkles size={12} /> {isSq ? "Mirë se erdhët" : "Welcome"}
                    </div>
                    <h2 className="mt-2 text-[22px] font-bold leading-tight tracking-tight">
                        {isSq ? "Këto janë disa hapa për të filluar" : "Here's how to get started"}
                    </h2>
                    <p className="mt-1 text-[13px] text-white/80">
                        {isSq
                            ? "Një udhëzim i shpejtë për funksionet kryesore të Akomodës."
                            : "A quick walkthrough of Akomoda's main features."}
                    </p>
                </div>

                {/* Body */}
                <div className="p-6">
                    {isSecurityStep ? (
                        <SecurityStep recs={recs} isSq={isSq} />
                    ) : (
                        <TipStep tip={tip} isSq={isSq} />
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
                    <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
                        {Array.from({ length: total }).map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === step ? "w-6 bg-[#F26322]" : "w-1.5 bg-slate-200"
                                }`}
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        {step > 0 && (
                            <button
                                onClick={back}
                                data-testid="onboarding-back"
                                className="flex items-center gap-1 rounded-lg px-3 py-2 text-[13px] font-semibold text-slate-600 transition hover:bg-slate-100"
                            >
                                <ArrowLeft size={13} /> {isSq ? "Kthehu" : "Back"}
                            </button>
                        )}
                        <button
                            onClick={dismiss}
                            data-testid="onboarding-skip"
                            className="rounded-lg px-3 py-2 text-[13px] font-medium text-slate-500 transition hover:text-slate-800"
                        >
                            {isSq ? "Kalo" : "Skip"}
                        </button>
                        <button
                            onClick={next}
                            data-testid="onboarding-next"
                            className="flex items-center gap-1.5 rounded-xl bg-[#F26322] px-4 py-2 text-[13px] font-bold text-white shadow-[0_6px_20px_-6px_rgba(242,99,34,.55)] transition hover:-translate-y-[1px] hover:bg-[#e05715]"
                        >
                            {step >= total - 1
                                ? (isSq ? "Përfundoj" : "Finish")
                                : (isSq ? "Vazhdo" : "Next")}
                            <ArrowRight size={13} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TipStep({ tip, isSq }) {
    if (!tip) return null;
    const Icon = ICON_MAP[tip.icon] || Sparkles;
    const title = isSq ? tip.title : (tip.title_en || tip.title);
    const body = isSq ? tip.body : (tip.body_en || tip.body);
    const cta = isSq ? tip.cta_label : (tip.cta_label_en || tip.cta_label);
    return (
        <div data-testid={`onboarding-tip-${tip.id}`}>
            <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-orange-50 text-[#F26322]">
                    <Icon size={22} strokeWidth={1.9} />
                </div>
                <div className="flex-1">
                    <h3 className="text-[17px] font-bold tracking-tight text-slate-900">{title}</h3>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">{body}</p>
                    {tip.cta_to && (
                        <Link
                            to={tip.cta_to}
                            className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#F26322] hover:underline"
                        >
                            {cta} <ArrowRight size={12} />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

function SecurityStep({ recs, isSq }) {
    return (
        <div data-testid="onboarding-security-step">
            <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <ShieldCheck size={22} strokeWidth={1.9} />
                </div>
                <div className="flex-1">
                    <h3 className="text-[17px] font-bold tracking-tight text-slate-900">
                        {isSq ? "Rrit sigurinë e llogarisë tënde" : "Level up your account security"}
                    </h3>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
                        {isSq
                            ? "Këto hapa janë opsionale por të rekomanduara — bëji tani ose më vonë nga menyja e Sigurisë."
                            : "These steps are optional but recommended — do them now, or later from Security settings."}
                    </p>
                </div>
            </div>
            <ul className="mt-5 space-y-2.5">
                {recs.map((r) => (
                    <li
                        key={r.id}
                        className={`flex items-center gap-3 rounded-xl border p-3 transition ${
                            r.done
                                ? "border-emerald-200 bg-emerald-50/60"
                                : "border-slate-200 bg-white hover:border-[#F26322]/40"
                        }`}
                        data-testid={`security-rec-${r.id}`}
                    >
                        <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${
                            r.done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                        }`}>
                            {r.done ? <CheckCircle2 size={14} /> : <Lock size={14} />}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-[13.5px] font-semibold text-slate-900">
                                {isSq ? r.title : (r.title_en || r.title)}
                            </div>
                            <div className="mt-0.5 truncate text-[12px] text-slate-500">
                                {isSq ? r.body : (r.body_en || r.body)}
                            </div>
                        </div>
                        {!r.done && r.cta_to && (
                            <Link
                                to={r.cta_to}
                                className="shrink-0 rounded-lg bg-[#F26322] px-3 py-1.5 text-[12px] font-bold text-white transition hover:bg-[#e05715]"
                            >
                                {isSq ? r.cta_label : (r.cta_label_en || r.cta_label)}
                            </Link>
                        )}
                        {r.done && (
                            <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-1 text-[10.5px] font-bold uppercase tracking-wider text-emerald-700">
                                {isSq ? "U bë" : "Done"}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
